<template>
  <q-card-section class="q-gutter-xs">
    <q-input
      :dark="isDarkModeGlobal"
      v-show="!isManualSkullPressureTransmission"
      outlined
      dense
      readonly
      for="Skull_pressure_transmission"
      label="Pressure transmission [-]"
      type="number"
      min="0"
      max="1"
      hide-bottom-space
      v-model="skull_pressure_transmission"
      :rules="[
        val => !!val || 'This field is required',
        val => val > 0 || 'Value must be > 0',
        val => val <= 1 || 'Value must be <= 1'
      ]"
    >
      <template #append>
        <q-icon
          name="edit"
          @click="calculatorStore.switchToManualSkullPressureTransmission(true)"
        >
          <q-tooltip>
            When modified, pressure transmission calculation based on US beam diameter and fundamental frequency of look-up table is overruled. Use the reset button to undo the overruling.
          </q-tooltip>
        </q-icon>
        <reference-popup-proxy
          :link="'https://doi.org/10.1016/j.brs.2022.12.005'"
          :text="`Reference: David Attali, Thomas Tiennot, Mark Schafer, Elsa Fouragnan, Jérôme Sallet, Charles F Caskey, Robert Chen, Ghazaleh Darmani, Ellen J. Bubrick, Christopher Butler, Charlotte J Stagg, Miriam Klein-Flügge, Lennart Verhagen, Seung-Schik Yoo, Kim Butts Pauly, Jean-Francois Aubry, Three-layer model with absorption for conservative estimation of the maximum acoustic transmission coefficient through the human skull for transcranial ultrasound stimulation, Brain Stimulation, Volume 16, Issue 1, 2023, Pages 48-55, ISSN 1935-861X, `"
        />
        <reference-popup-proxy
          :icon="'help'"
          tooltip="Show note"
          :text="`When modified, pressure transmission calculation based on US beam diameter and fundamental frequency of look-up table is overruled. Use the reset button to undo the overruling.`"
        />
      </template>
    </q-input>

    <q-input
      :dark="isDarkModeGlobal"
      v-show="isManualSkullPressureTransmission"
      dense
      filled
      for="Skull_pressure_transmission"
      label="Pressure transmission [-]"
      type="number"
      min="0"
      max="1"
      v-model="skull_pressure_transmission_manual"
      step="0.01"
      :rules="[
        val => !!val || 'This field is required',
        val => val > 0 || 'Value must be > 0',
        val => val <= 1 || 'Value must be <= 1'
      ]"
      @update:modelValue="executeCalculation(skull_pressure_transmission_manual, 'pr')"
      hide-bottom-space
    >
      <template #append>
        <q-icon
          name="refresh"
          @click="calculatorStore.switchToManualSkullPressureTransmission(false)"
        >
          <q-tooltip>
            When modified, pressure transmission calculation based on US beam diameter and fundamental frequency of look-up table is overruled. Use the reset button to undo the overruling.
          </q-tooltip>
        </q-icon>

        <reference-popup-proxy
          :link="'https://doi.org/10.1016/j.brs.2022.12.005'"
          :text="`Reference: David Attali, Thomas Tiennot, Mark Schafer, Elsa Fouragnan, Jérôme Sallet, Charles F Caskey, Robert Chen, Ghazaleh Darmani, Ellen J. Bubrick, Christopher Butler, Charlotte J Stagg, Miriam Klein-Flügge, Lennart Verhagen, Seung-Schik Yoo, Kim Butts Pauly, Jean-Francois Aubry, Three-layer model with absorption for conservative estimation of the maximum acoustic transmission coefficient through the human skull for transcranial ultrasound stimulation, Brain Stimulation, Volume 16, Issue 1, 2023, Pages 48-55, ISSN 1935-861X, `"
        />

        <reference-popup-proxy
          :icon="'help'"
          tooltip="Show note"
          :text="`When modified, pressure transmission calculation based on US beam diameter and fundamental frequency of look-up table is overruled. Use the reset button to undo the overruling.`"
        />
      </template>
    </q-input>
  </q-card-section>

  <q-dialog v-model="showManualSkullPressureTransmissionMessage">
      <q-card :dark="isDarkModeGlobal">
        <q-card-section>
          <div class="text-h6">Warning</div>
        </q-card-section>

        <q-card-section class="q-pt-none">
          When modified, pressure transmission calculation based on US beam diameter and fundamental frequency of look-up table is overruled. Use the reset button to undo the overruling.
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="OK" color="primary" v-close-popup />
        </q-card-actions>
      </q-card>
    </q-dialog>

</template>
<script setup lang="ts">
import {storeToRefs} from "pinia"
import {useCalculatorStore} from '../stores/calculator.store'
import {computed, ref, watch} from "vue"
import ReferencePopupProxy from "./PopupProxyLiteratureReference.vue"

const calculatorStore = useCalculatorStore()
const {
  focal_depth_mm,
  thickness_of_skull_mm,
  thickness_of_scalp_mm,
  coupling_thickness_mm,
  isManualSkullPressureTransmission,
  isDarkModeGlobal,
  skull_pressure_transmission,
  skull_pressure_transmission_manual,
  loadingValue
} = storeToRefs(calculatorStore)

const showManualSkullPressureTransmissionMessage = ref(false)

watch(isManualSkullPressureTransmission, (newVal) => {
  if (isManualSkullPressureTransmission.value) {
    showManualSkullPressureTransmissionMessage.value = true
  }
})

const minimumValueFocalDepth = computed(() => +coupling_thickness_mm.value + +thickness_of_scalp_mm.value + +thickness_of_skull_mm.value)
const blurActiveInput = () => {
  const activeElement = document.activeElement;
  if (activeElement && activeElement.tagName === 'INPUT') {
    activeElement.blur()
  }
};

let debounceTimeout = null // Timeout reference for debounce
let interval = null // Timeout reference for interval
const loading = ref(false) // Tracks spinner visibility

let totalTime = 3000
let intervalTime = 100 // Time between each count

function executeCalculation(val: number, calcType: string) {
  if (debounceTimeout) {
    clearTimeout(debounceTimeout);
    clearInterval(interval)
    loadingValue.value = 0
  }

  loading.value = true;
  interval = setInterval(() => {
  loadingValue.value += 0.1;
    if (loadingValue.value > 3) {
      clearInterval(interval); // Stop the interval when count reaches 100
      loadingValue.value = 0
    }
  }, intervalTime);
      // Start debounce timer
  debounceTimeout = setTimeout(() => {
        // After debounce delay
    loading.value = false;
    if (val > 0 && focal_depth_mm.value >= minimumValueFocalDepth.value) {
      switch (calcType) {
        case 'acoustic_power':
          calculatorStore.updateByAcousticPower()
          break
        case 'transducer_efficiency':
          calculatorStore.updateByElectricalPowerOrTransducerEfficiency()
          break
        case 'electrical_power':
          calculatorStore.updateByElectricalPowerOrTransducerEfficiency()
          break
        case 'pr':
          calculatorStore.updateByPr()
          break
        case 'MI':
          calculatorStore.updateByMI()
          break
        case 'non_derated_MI':
          calculatorStore.updateByNonDeratedMI()
          break
        default:
          calculatorStore.updateByIsppa()
          break
      }
      calculatorStore.clearNotifications()

      blurActiveInput()
    } else {
      console.error("empty value or 0 not allowed")
    }
  }, totalTime);
}
</script>
